import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from './useNotifications';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { useMessagesActive } from '@/context/MessagesActiveContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useBrowserNotifications } from './useBrowserNotifications';
import { openNotificationSheet } from '@/lib/notification-sheet-bus';

export const useNotificationToasts = () => {
  const { notifications, preferences, markAsRead } = useNotifications();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isViewingMessages, activeConnectionId } = useMessagesActive();
  const { sendBrowserNotification } = useBrowserNotifications();
  const previousNotificationIds = useRef(new Set<string>());
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Don't process notifications if user is null (during logout or before login)
    if (!user) {
      return;
    }

    if (isInitialLoad.current) {
      // Wait until we have the first real batch of notifications, then treat them as "already seen"
      if (notifications.length === 0) {
        return;
      }

      notifications.forEach(notification => {
        previousNotificationIds.current.add(notification.id);
      });
      isInitialLoad.current = false;
      return;
    }

    // Check for new notifications that weren't in the previous set
    // Also filter out notifications sent by the current user
    const newNotifications = notifications.filter(
      notification => {
        // Skip if already processed
        if (previousNotificationIds.current.has(notification.id)) return false;
        
        // Skip if already read
        if (notification.is_read) return false;
        
        // Skip if user sent this notification to themselves (safety check)
        if (notification.metadata?.sender_id === user?.id) {
          console.log('🚫 Filtering out self-sent notification:', notification.id, 'sender:', notification.metadata?.sender_id, 'user:', user?.id);
          return false;
        }
        
        // Skip connection-related notifications from toasts (they'll still appear in notification center)
        if (notification.category === 'connection' || notification.title === 'New Connection Request') {
          console.log('🚫 Filtering out connection notification from toast:', notification.id, 'category:', notification.category, 'title:', notification.title);
          return false;
        }
        
        return true;
      }
    );

    newNotifications.forEach(notification => {
      // Only show toast if push notifications are enabled
      if (!preferences.push_notifications) return;

      const getVariant = (): "default" | "destructive" => {
        switch (notification.type) {
          case 'error':
            return 'destructive';
          default:
            return 'default';
        }
      };

      const truncatedMessage = notification.message.length > 100 
        ? notification.message.substring(0, 100) + '...' 
        : notification.message;
      
      toast({
        title: notification.title,
        description: truncatedMessage,
        variant: getVariant(),
        duration: 8000,
        action: (
          <button
            onClick={() => {
              markAsRead(notification.id);
              // Use action_url for specific navigation if provided; otherwise open the global notification sheet.
              if (notification.action_url && notification.action_url.startsWith('/')) {
                navigate(notification.action_url);
              } else {
                openNotificationSheet();
              }
            }}
            className="inline-flex h-8 shrink-0 items-center justify-center rounded-none border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            View
          </button>
        ),
      });

      // Send browser notification when tab is in the background
      sendBrowserNotification(notification.title, {
        body: truncatedMessage,
        tag: `notification-${notification.id}`,
        data: {
          url: notification.action_url && notification.action_url.startsWith('/')
            ? notification.action_url
            : '/notifications',
        },
      });

      previousNotificationIds.current.add(notification.id);
    });
  }, [notifications, preferences.push_notifications, toast, navigate, markAsRead, user]);

  // Real-time listener for attorney messages to clients
  useEffect(() => {
    if (!user) return;

    console.log('[NotificationToasts] Setting up real-time listener for attorney messages, user:', user.email);

    const messagesChannel = supabase
      .channel('client-attorney-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'attorney_messages'
        },
        async (payload) => {
          console.log('[NotificationToasts] New attorney message received:', payload);
          
          const message = payload.new;
          
          // Only show notification if message is FROM attorney (not from client)
          if (message.from_client) {
            console.log('[NotificationToasts] Skipping - message is from client');
            return;
          }
          
          if (!message.connection_id) {
            console.log('[NotificationToasts] Skipping - no connection_id');
            return;
          }
          
          // Check if this message is for a connection involving this client
          // Use robust matching: check by user_id AND email
          const { data: connection } = await supabase
            .from('connections')
            .select('email, name, lawyer_id, user_id')
            .eq('id', message.connection_id)
            .single();
          
          console.log('[NotificationToasts] Connection data:', connection);
          console.log('[NotificationToasts] Current user:', { id: user.id, email: user.email });
          
          if (!connection) {
            console.log('[NotificationToasts] Connection not found');
            return;
          }
          
          // Robust matching: check both user_id and email (case-insensitive)
          const isUserConnection =
            connection.user_id === user.id || 
            connection.email?.toLowerCase() === user.email?.toLowerCase();
          
          console.log('[NotificationToasts] Match check:', {
            connection_user_id: connection.user_id,
            current_user_id: user.id,
            connection_email: connection.email,
            user_email: user.email,
            isMatch: isUserConnection
          });
          
          if (!isUserConnection) {
            console.log('[NotificationToasts] Message not for current user');
            return;
          }

          // Suppress toast if user is already viewing this conversation
          if (isViewingMessages && activeConnectionId === message.connection_id) {
            console.log('[NotificationToasts] Suppressing toast - user is viewing this conversation');
            // Still invalidate queries so message appears in the thread
            queryClient.invalidateQueries({ queryKey: ['client-messages'] });
            return;
          }
          
          // Get attorney name - lawyer_id on connections is the lawyer record id
          const { data: lawyer } = await supabase
            .from('lawyers')
            .select('first_name, last_name')
            .eq('id', connection.lawyer_id)
            .single();
          
          const attorneyName = lawyer 
            ? `${lawyer.first_name} ${lawyer.last_name}`
            : 'Your attorney';
          
          console.log('[NotificationToasts] Showing toast for attorney:', attorneyName);
          
          // Show toast with action to view message
          toast({
            title: `New message from ${attorneyName}`,
            description: message.subject || 'You have a new message',
            duration: 8000,
            action: (
              <button
                onClick={() => navigate(`/client-portal?tab=messages&connection=${message.connection_id}`)}
                className="inline-flex h-8 shrink-0 items-center justify-center rounded-none border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                View
              </button>
            ),
          });
          
          // Send browser notification when tab is in the background
          sendBrowserNotification(`New message from ${attorneyName}`, {
            body: message.subject || 'You have a new message',
            tag: `message-${message.id}`,
            data: { url: `/client-portal?tab=messages&connection=${message.connection_id}` },
          });
          
          // Invalidate client messages query
          queryClient.invalidateQueries({ queryKey: ['client-messages'] });
        }
      )
      .subscribe();

    return () => {
      console.log('[NotificationToasts] Cleaning up attorney messages listener');
      supabase.removeChannel(messagesChannel);
    };
  }, [user, toast, navigate, queryClient, isViewingMessages, activeConnectionId, sendBrowserNotification]);

  // Real-time listener for client/in-house messages TO attorneys
  useEffect(() => {
    if (!user) return;

    console.log('[NotificationToasts] Setting up real-time listener for incoming client messages to attorney, user:', user.email);

    const attorneyInboundChannel = supabase
      .channel('attorney-inbound-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'attorney_messages'
        },
        async (payload) => {
          const message = payload.new;

          // Only notify if message is FROM a client/in-house user
          if (!message.from_client) return;

          // Only notify if the message belongs to this attorney
          if (message.attorney_user_id !== user.id) return;

          if (!message.connection_id) return;

          // Suppress toast if user is already viewing this conversation
          if (isViewingMessages && activeConnectionId === message.connection_id) {
            queryClient.invalidateQueries({ queryKey: ['recent-messages'] });
            return;
          }

          // Get sender name from the connection
          const { data: connection } = await supabase
            .from('connections')
            .select('name, email')
            .eq('id', message.connection_id)
            .single();

          const senderName = connection?.name || connection?.email || 'A client';

          toast({
            title: `New message from ${senderName}`,
            description: message.subject || 'You have a new message',
            duration: 8000,
            action: (
              <button
                onClick={() => navigate(`/attorney-portal?tab=messages&connection=${message.connection_id}`)}
                className="inline-flex h-8 shrink-0 items-center justify-center rounded-none border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                View
              </button>
            ),
          });

          sendBrowserNotification(`New message from ${senderName}`, {
            body: message.subject || 'You have a new message',
            tag: `message-${message.id}`,
            data: { url: `/attorney-portal?tab=messages&connection=${message.connection_id}` },
          });

          // Refresh message lists
          queryClient.invalidateQueries({ queryKey: ['recent-messages'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(attorneyInboundChannel);
    };
  }, [user, toast, navigate, queryClient, isViewingMessages, activeConnectionId, sendBrowserNotification]);

  return null;
};